import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Check, AlertCircle, Eye, EyeOff, ArrowLeft, Building2, Briefcase, User } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import type { OAuthProvider } from '../services/auth';
import { getAllProvinces, getDistricts } from '../data/regions';
import MainTabsNav from '../components/MainTabsNav';

// 약관 데이터
const terms = [
  {
    id: 'service',
    title: '서비스 이용약관',
    required: true,
    content: `제1조 (목적)
이 약관은 스탬핏(이하 "회사")이 제공하는 피트니스 지도자 자격 인증 및 구인구직 서비스(이하 "서비스")의 이용에 관한 조건 및 절차, 회사와 회원의 권리, 의무 및 책임사항 등을 규정함을 목적으로 합니다.

제2조 (용어의 정의)
1. "서비스"란 회사가 제공하는 피트니스 관련 자격 인증, 교육 정보, 구인구직 등의 온라인 서비스를 말합니다.
2. "회원"이란 이 약관에 동의하고 회사와 서비스 이용계약을 체결한 자를 말합니다.

제3조 (약관의 효력 및 변경)
1. 이 약관은 서비스를 이용하고자 하는 모든 회원에게 적용됩니다.
2. 회사는 필요한 경우 관련 법령을 위배하지 않는 범위에서 이 약관을 변경할 수 있습니다.`,
  },
  {
    id: 'privacy',
    title: '개인정보 처리방침',
    required: true,
    content: `스탬핏(이하 "회사")은 개인정보보호법에 따라 이용자의 개인정보 보호 및 권익을 보호합니다.

1. 개인정보의 수집 및 이용 목적
- 회원 가입 및 관리: 회원제 서비스 이용에 따른 본인확인, 개인식별
- 서비스 제공: 자격 인증, 교육 정보 제공, 구인구직 서비스 등

2. 수집하는 개인정보 항목
- 필수항목: 이메일, 이름, 비밀번호, 휴대폰번호
- 선택항목: 프로필 사진, 자격증 정보

3. 개인정보의 보유 및 이용기간
- 회원 탈퇴 시까지`,
  },
  {
    id: 'marketing',
    title: '마케팅 정보 수신 동의',
    required: false,
    content: `스탬핏에서 제공하는 이벤트, 프로모션, 신규 서비스 안내 등의 마케팅 정보를 수신하는 것에 동의합니다.

- 수신 정보: 신규 교육 과정 안내, 할인 이벤트, 서비스 업데이트 소식
- 수신 방법: 이메일, SMS, 앱 푸시 알림

※ 마케팅 정보 수신에 동의하지 않아도 서비스 이용에는 제한이 없습니다.`,
  },
];

type Step = 'type' | 'method' | 'terms' | 'info';
// provider = 교육 기관 (수업/인증서), employer = 기업 회원 (구인)
type MemberType = 'user' | 'provider' | 'employer';

export default function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginSuccess, signIn, loading: oauthLoading } = useAuthStore();

  const handleOAuthSignup = async (provider: OAuthProvider) => {
    try {
      await signIn(provider);
    } catch (err) {
      console.error('OAuth signup error:', err);
    }
  };

  // URL 파라미터에서 회원 유형 확인
  const typeFromUrl = searchParams.get('type') as MemberType | null;

  // Step 관리 (일반 회원만 '가입 방법 선택' 단계를 거침 — 교육 기관/기업 회원은 이메일 가입만 가능해 바로 약관 동의로)
  const [step, setStep] = useState<Step>(
    typeFromUrl ? (typeFromUrl === 'user' ? 'method' : 'terms') : 'type'
  );
  const [memberType, setMemberType] = useState<MemberType>(typeFromUrl || 'user');

  // URL 파라미터 변경 시 memberType 동기화
  useEffect(() => {
    if (typeFromUrl) {
      setMemberType(typeFromUrl);
      if (step === 'type') {
        setStep(typeFromUrl === 'user' ? 'method' : 'terms');
      }
    }
  }, [typeFromUrl]);

  // 약관 동의 상태
  const [agreements, setAgreements] = useState<Record<string, boolean>>({
    service: false,
    privacy: false,
    marketing: false,
  });

  // 회원 정보 상태
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    passwordConfirm: '',
    name: '',
    firstName: '',
    lastName: '',
    gender: '' as '' | 'male' | 'female',
    birthYear: '',
    birthMonth: '',
    birthDay: '',
    phone: '',
    // 지역 정보
    province: '',
    district: '',
    // 기업 회원 전용 필드
    companyName: '',
    ceoName: '',
    ceoFirstName: '',
    ceoLastName: '',
    businessCategory: 'FITNESS' as 'FITNESS' | 'PILATES',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  // 필드별 에러 상태
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // 공통 상태
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 교육 기관 가입용: 사업자등록증/민간자격증 파일 (provider만 필수)
  const [certificateFile, setCertificateFile] = useState<File | null>(null);

  const handleCertificateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setCertificateFile(null);
      return;
    }
    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setError('PDF, JPG, PNG 파일만 업로드 가능합니다.');
      e.target.value = '';
      setCertificateFile(null);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('파일 크기는 10MB 이하여야 합니다.');
      e.target.value = '';
      setCertificateFile(null);
      return;
    }
    setError(null);
    setCertificateFile(file);
  };

  // 이메일 유효성 검사
  const validateEmail = (email: string): string | null => {
    if (!email) return '이메일을 입력해주세요.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return '올바른 이메일 형식이 아닙니다.';
    return null;
  };

  // 비밀번호 유효성 검사
  const validatePassword = (password: string): string | null => {
    if (!password) return '비밀번호를 입력해주세요.';
    if (password.length < 8) return '8자 이상 입력해주세요.';
    if (!/[a-zA-Z]/.test(password)) return '영문을 포함해주세요.';
    if (!/[0-9]/.test(password)) return '숫자를 포함해주세요.';
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) return '특수문자를 포함해주세요.';
    return null;
  };

  // 비밀번호 확인 유효성 검사
  const validatePasswordConfirm = (passwordConfirm: string): string | null => {
    if (!passwordConfirm) return '비밀번호를 다시 입력해주세요.';
    if (passwordConfirm !== formData.password) return '비밀번호가 일치하지 않습니다.';
    return null;
  };

  // First Name 유효성 검사
  const validateFirstName = (firstName: string): string | null => {
    if (!firstName) return 'First Name을 입력해주세요.';
    if (!/^[a-zA-Z\s]+$/.test(firstName)) return '영문만 입력해주세요.';
    return null;
  };

  // Last Name 유효성 검사
  const validateLastName = (lastName: string): string | null => {
    if (!lastName) return 'Last Name을 입력해주세요.';
    if (!/^[a-zA-Z\s]+$/.test(lastName)) return '영문만 입력해주세요.';
    return null;
  };

  // 필드 blur 핸들러
  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));

    let fieldError: string | null = null;
    switch (field) {
      case 'email':
        fieldError = validateEmail(formData.email);
        break;
      case 'password':
        fieldError = validatePassword(formData.password);
        if (touched.passwordConfirm) {
          const confirmError = validatePasswordConfirm(formData.passwordConfirm);
          setFieldErrors((prev) => ({
            ...prev,
            passwordConfirm: confirmError || '',
          }));
        }
        break;
      case 'passwordConfirm':
        fieldError = validatePasswordConfirm(formData.passwordConfirm);
        break;
      case 'firstName':
        fieldError = validateFirstName(formData.firstName);
        break;
      case 'lastName':
        fieldError = validateLastName(formData.lastName);
        break;
      case 'ceoFirstName':
        fieldError = validateFirstName(formData.ceoFirstName);
        break;
      case 'ceoLastName':
        fieldError = validateLastName(formData.ceoLastName);
        break;
    }

    setFieldErrors((prev) => ({ ...prev, [field]: fieldError || '' }));
  };

  // 약관 관련 함수
  const allRequiredAgreed = terms.filter((t) => t.required).every((t) => agreements[t.id]);
  const allAgreed = terms.every((t) => agreements[t.id]);

  const handleToggle = (id: string) => {
    setAgreements((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleToggleAll = () => {
    const newValue = !allAgreed;
    const newAgreements: Record<string, boolean> = {};
    terms.forEach((t) => {
      newAgreements[t.id] = newValue;
    });
    setAgreements(newAgreements);
  };

  const handleSelectType = (type: MemberType) => {
    setMemberType(type);
    setStep(type === 'user' ? 'method' : 'terms');
  };

  const handleNextStep = () => {
    if (!allRequiredAgreed) {
      setError('필수 약관에 모두 동의해주세요.');
      return;
    }
    setError(null);
    setStep('info');
  };

  const handleBack = () => {
    if (step === 'info') {
      setStep('terms');
    } else if (step === 'terms') {
      if (isBusinessMember) {
        if (typeFromUrl) {
          navigate('/login');
        } else {
          setStep('type');
        }
      } else {
        setStep('method');
      }
    } else if (step === 'method') {
      if (typeFromUrl) {
        navigate('/login');
      } else {
        setStep('type');
      }
    }
    setError(null);
  };

  // 폼 입력 핸들러
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 지역 선택 핸들러
  const handleProvinceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const province = e.target.value;
    setFormData((prev) => ({ ...prev, province, district: '' }));
  };

  const handleDistrictChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const district = e.target.value;
    setFormData((prev) => ({ ...prev, district }));
  };

  // 이름 합치기 및 정리 헬퍼 함수
  const cleanAndConcatenateName = (firstName: string, lastName: string): string => {
    const cleanFirst = firstName.trim().replace(/\s+/g, ' ');
    const cleanLast = lastName.trim().replace(/\s+/g, ' ');
    if (!cleanLast) return cleanFirst; // Last Name이 비어있으면 First Name만
    return `${cleanFirst} ${cleanLast}`.trim();
  };

  // 전체 유효성 검사
  const validateForm = () => {
    const emailError = validateEmail(formData.email);
    if (emailError) return emailError;

    const passwordError = validatePassword(formData.password);
    if (passwordError) return `비밀번호: ${passwordError}`;

    const confirmError = validatePasswordConfirm(formData.passwordConfirm);
    if (confirmError) return confirmError;

    if (!formData.name) return '이름을 입력해주세요.';

    // 일반 회원: 본인 영문 이름 필수 / 교육 기관·기업 회원: 대표자 영문 이름 필수
    if (memberType === 'provider' || memberType === 'employer') {
      // 사업자 회원(교육 기관/기업 회원) 추가 검증 — 두 유형의 입력 필드는 동일
      if (!formData.companyName) return '기업명을 입력해주세요.';
      if (!formData.ceoName) return '대표자 이름을 입력해주세요.';
      const ceoFirstNameError = validateFirstName(formData.ceoFirstName);
      if (ceoFirstNameError) return `대표자 First Name: ${ceoFirstNameError}`;
      const ceoLastNameError = validateLastName(formData.ceoLastName);
      if (ceoLastNameError) return `대표자 Last Name: ${ceoLastNameError}`;
      // 교육 기관(provider)은 사업자등록증/민간자격증 파일 필수
      if (memberType === 'provider' && !certificateFile) {
        return '사업자등록증 또는 민간자격증을 업로드해주세요.';
      }
    } else {
      const firstNameError = validateFirstName(formData.firstName);
      if (firstNameError) return `First Name: ${firstNameError}`;
      const lastNameError = validateLastName(formData.lastName);
      if (lastNameError) return `Last Name: ${lastNameError}`;
    }

    if (!formData.gender) return '성별을 선택해주세요.';
    if (!formData.birthYear || !formData.birthMonth || !formData.birthDay) return '생년월일을 입력해주세요.';

    if (!formData.phone) return '휴대폰 번호를 입력해주세요.';
    if (!/^01[0-9]-?[0-9]{3,4}-?[0-9]{4}$/.test(formData.phone.replace(/-/g, '')))
      return '올바른 휴대폰 번호 형식이 아닙니다.';

    // 지역 정보 검증
    if (!formData.province) return '시/도를 선택해주세요.';
    if (!formData.district) return '시/군/구를 선택해주세요.';

    return null;
  };

  // 회원가입 제출
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const isBiz = memberType === 'provider' || memberType === 'employer';
      const ceoNameEn = isBiz
        ? cleanAndConcatenateName(formData.ceoFirstName, formData.ceoLastName)
        : '';
      const nameEn = isBiz
        ? ''
        : cleanAndConcatenateName(formData.firstName, formData.lastName);
      const birthDate = `${formData.birthYear}-${formData.birthMonth.padStart(2, '0')}-${formData.birthDay.padStart(2, '0')}`;

      let response: Response;

      if (memberType === 'provider' && certificateFile) {
        // 교육 기관: 사업자등록증/민간자격증 첨부 → multipart/form-data
        const fd = new FormData();
        fd.set('email', formData.email);
        fd.set('password', formData.password);
        fd.set('name', formData.name);
        fd.set('nameEn', nameEn);
        fd.set('gender', formData.gender);
        fd.set('birthDate', birthDate);
        fd.set('phone', formData.phone);
        fd.set('province', formData.province);
        fd.set('district', formData.district);
        fd.set('termsAgreed', String(agreements.service));
        fd.set('privacyAgreed', String(agreements.privacy));
        fd.set('marketingAgreed', String(agreements.marketing));
        fd.set('memberType', memberType);
        fd.set('companyName', formData.companyName);
        fd.set('ceoName', formData.ceoName);
        fd.set('ceoNameEn', ceoNameEn);
        fd.set('businessCategory', formData.businessCategory);
        fd.set('certificate', certificateFile);
        response = await fetch('/api/v1/auth/signup', {
          method: 'POST',
          credentials: 'include',
          body: fd,
        });
      } else {
        // 일반 회원/기업 회원: 기존 JSON 흐름
        response = await fetch('/api/v1/auth/signup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            name: formData.name,
            nameEn,
            gender: formData.gender,
            birthDate,
            phone: formData.phone,
            province: formData.province,
            district: formData.district,
            termsAgreed: agreements.service,
            privacyAgreed: agreements.privacy,
            marketingAgreed: agreements.marketing,
            memberType,
            companyName: isBiz ? formData.companyName : undefined,
            ceoName: isBiz ? formData.ceoName : undefined,
            ceoNameEn: isBiz ? ceoNameEn : undefined,
            businessCategory: isBiz ? formData.businessCategory : undefined,
          }),
        });
      }

      const data = await response.json();
      console.log('Signup response:', { status: response.status, ok: response.ok, data });

      if (!response.ok) {
        throw new Error(data.error || '회원가입에 실패했습니다.');
      }

      console.log('Navigating to /email-verification-pending with email:', formData.email);
      // 이메일 인증 대기 페이지로 이동 (세션은 생성하지 않음)
      navigate('/email-verification-pending', {
        replace: true,
        state: { email: formData.email },
      });
      console.log('Navigation called');
    } catch (err) {
      setError(err instanceof Error ? err.message : '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 사업자 회원(교육 기관/기업 회원) 공통 플래그 — UI는 동일한 accent 컬러 사용
  const isBusinessMember = memberType === 'provider' || memberType === 'employer';
  const isProvider = isBusinessMember; // 기존 isProvider 사용처를 그대로 두기 위한 alias (UI 분기는 동일)
  const memberTypeLabel =
    memberType === 'provider' ? '교육 기관' : memberType === 'employer' ? '기업 회원' : '일반';

  return (
    <>
      <MainTabsNav />
      <div className="py-8 px-4">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-2xl shadow-sm p-8">
          {/* Header */}
          <div className="mb-8">
            {step !== 'type' && (
              <button
                onClick={handleBack}
                className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>이전</span>
              </button>
            )}

            {step === 'type' && (
              <>
                <h1 className="text-2xl font-bold text-gray-900 text-center">회원가입</h1>
                <p className="text-gray-600 text-center mt-2">회원 유형을 선택해주세요</p>
              </>
            )}

            {step !== 'type' && (
              <>
                <div className="flex items-center justify-center gap-2 mb-2">
                  {memberType === 'provider' ? (
                    <Building2 className="w-6 h-6 text-accent" />
                  ) : memberType === 'employer' ? (
                    <Briefcase className="w-6 h-6 text-emerald-600" />
                  ) : (
                    <User className="w-6 h-6 text-primary" />
                  )}
                  <h1 className="text-2xl font-bold text-gray-900">
                    {memberTypeLabel} 회원가입
                  </h1>
                </div>
                <p className="text-gray-600 text-center mt-2">
                  {step === 'method'
                    ? '가입 방법을 선택해주세요.'
                    : step === 'terms'
                    ? '서비스 이용을 위해 약관에 동의해주세요.'
                    : '회원 정보를 입력해주세요.'}
                </p>

                {/* Step Indicator (약관 동의/정보 입력 단계에서만 표시) */}
                {step !== 'method' && (
                  <div className="flex items-center justify-center gap-2 mt-6">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        step === 'terms'
                          ? isProvider ? 'bg-accent text-white' : 'bg-primary text-white'
                          : isProvider ? 'bg-accent/20 text-accent' : 'bg-primary/20 text-primary'
                      }`}
                    >
                      1
                    </div>
                    <div className="w-8 h-0.5 bg-gray-200" />
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        step === 'info'
                          ? isProvider ? 'bg-accent text-white' : 'bg-primary text-white'
                          : 'bg-gray-200 text-gray-400'
                      }`}
                    >
                      2
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Step 0: Member Type Selection */}
          {step === 'type' && (
            <div className="space-y-4">
              {/* 일반 회원 */}
              <button
                onClick={() => handleSelectType('user')}
                className="w-full flex items-center gap-4 p-6 border-2 border-gray-200 rounded-xl hover:border-primary hover:bg-primary/5 transition-all group"
              >
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <User className="w-7 h-7 text-primary" />
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-gray-900 text-lg">일반 회원</h3>
                  <p className="text-gray-500 text-sm">피트니스 강사, 트레이너</p>
                </div>
              </button>

              {/* 교육 기관 */}
              <button
                onClick={() => handleSelectType('provider')}
                className="w-full flex items-center gap-4 p-6 border-2 border-gray-200 rounded-xl hover:border-accent hover:bg-accent/5 transition-all group"
              >
                <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                  <Building2 className="w-7 h-7 text-accent" />
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-gray-900 text-lg">교육 기관</h3>
                  <p className="text-gray-500 text-sm">교육 업체, 자격 발급 기관 (수업/인증서)</p>
                </div>
              </button>

              {/* 기업 회원 (구인) */}
              <button
                onClick={() => handleSelectType('employer')}
                className="w-full flex items-center gap-4 p-6 border-2 border-gray-200 rounded-xl hover:border-emerald-500 hover:bg-emerald-50 transition-all group"
              >
                <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                  <Briefcase className="w-7 h-7 text-emerald-600" />
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-gray-900 text-lg">기업 회원</h3>
                  <p className="text-gray-500 text-sm">채용 공고 등록, 이력서 열람</p>
                </div>
              </button>
            </div>
          )}

          {/* Step 0 (일반 회원 전용): 가입 방법 선택 */}
          {step === 'method' && (
            <>
              <div className="space-y-3 mb-6">
                <p className="text-center text-gray-500 text-sm mb-4">간편 가입</p>

                {/* Google */}
                <button
                  type="button"
                  onClick={() => handleOAuthSignup('google')}
                  disabled={oauthLoading}
                  className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 font-semibold px-6 py-3 rounded-lg border border-gray-300 transition-colors"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google로 시작하기
                </button>

                {/* Kakao */}
                <button
                  type="button"
                  onClick={() => handleOAuthSignup('kakao')}
                  disabled={oauthLoading}
                  className="w-full flex items-center justify-center gap-3 bg-[#FEE500] hover:bg-[#FDD800] disabled:opacity-50 disabled:cursor-not-allowed text-[#000000]/85 font-semibold px-6 py-3 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 3c5.799 0 10.5 3.664 10.5 8.185 0 4.52-4.701 8.184-10.5 8.184a13.5 13.5 0 01-1.727-.11l-4.408 2.883c-.501.265-.678.236-.472-.413l.892-3.678c-2.88-1.46-4.785-3.99-4.785-6.866C1.5 6.665 6.201 3 12 3z"/>
                  </svg>
                  카카오로 시작하기
                </button>
              </div>

              {/* Divider */}
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">또는</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setStep('terms')}
                className="w-full font-semibold px-6 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                이메일로 가입하기
              </button>
            </>
          )}

          {/* Step 1: Terms Agreement */}
          {step === 'terms' && (
            <>
              {/* All Agree */}
              <div
                onClick={handleToggleAll}
                className={`flex items-center gap-3 p-4 rounded-lg cursor-pointer transition-colors mb-4 ${
                  isProvider ? 'bg-accent/5 hover:bg-accent/10' : 'bg-primary/5 hover:bg-primary/10'
                }`}
              >
                <div
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                    allAgreed
                      ? isProvider ? 'bg-accent border-accent' : 'bg-primary border-primary'
                      : 'border-gray-300'
                  }`}
                >
                  {allAgreed && <Check className="w-4 h-4 text-white" />}
                </div>
                <span className="font-medium text-gray-900">전체 동의</span>
              </div>

              {/* Terms List */}
              <div className="space-y-4 mb-8">
                {terms.map((term) => (
                  <div key={term.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div
                      onClick={() => handleToggle(term.id)}
                      className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                          agreements[term.id]
                            ? isProvider ? 'bg-accent border-accent' : 'bg-primary border-primary'
                            : 'border-gray-300'
                        }`}
                      >
                        {agreements[term.id] && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className="text-gray-900 font-medium">
                        {term.required ? (
                          <span className="text-red-500 mr-1">[필수]</span>
                        ) : (
                          <span className="text-gray-400 mr-1">[선택]</span>
                        )}
                        {term.title}
                      </span>
                    </div>
                    <div className="px-4 pb-4">
                      <div className="bg-gray-50 rounded-lg p-4 max-h-48 overflow-y-auto">
                        <pre className="text-sm text-gray-600 whitespace-pre-wrap font-sans leading-relaxed">
                          {term.content}
                        </pre>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={handleNextStep}
                disabled={!allRequiredAgreed}
                className={`w-full py-4 rounded-lg font-medium text-white transition-colors ${
                  allRequiredAgreed
                    ? isProvider ? 'bg-accent hover:bg-accent/90' : 'bg-primary hover:bg-primary-600'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                다음
              </button>
            </>
          )}

          {/* Step 2: User Info */}
          {step === 'info' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* 사업자 회원(교육 기관/기업 회원) 공통 필드 */}
              {isProvider && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      기업명 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      placeholder="스탬핏 교육원"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                    />
                  </div>
                  {/* 업종 카테고리 선택 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      업종 카테고리 <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-4">
                      <label
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border rounded-lg cursor-pointer transition-colors ${
                          formData.businessCategory === 'FITNESS'
                            ? 'border-accent bg-accent/5 text-accent'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <input
                          type="radio"
                          name="businessCategory"
                          value="FITNESS"
                          checked={formData.businessCategory === 'FITNESS'}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <span className="font-medium">피트니스</span>
                      </label>
                      <label
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border rounded-lg cursor-pointer transition-colors ${
                          formData.businessCategory === 'PILATES'
                            ? 'border-accent bg-accent/5 text-accent'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <input
                          type="radio"
                          name="businessCategory"
                          value="PILATES"
                          checked={formData.businessCategory === 'PILATES'}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <span className="font-medium">필라테스</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      대표자 이름 (한글) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="ceoName"
                      value={formData.ceoName}
                      onChange={handleInputChange}
                      placeholder="홍길동"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      대표자 First Name (이름) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="ceoFirstName"
                      value={formData.ceoFirstName}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur('ceoFirstName')}
                      placeholder="Gildong"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none ${
                        touched.ceoFirstName && fieldErrors.ceoFirstName ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {touched.ceoFirstName && fieldErrors.ceoFirstName && (
                      <p className="mt-1 text-sm text-red-500">{fieldErrors.ceoFirstName}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      대표자 Last Name (성) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="ceoLastName"
                      value={formData.ceoLastName}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur('ceoLastName')}
                      placeholder="Hong"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none ${
                        touched.ceoLastName && fieldErrors.ceoLastName ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {touched.ceoLastName && fieldErrors.ceoLastName && (
                      <p className="mt-1 text-sm text-red-500">{fieldErrors.ceoLastName}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">예: Gildong Hong</p>
                  </div>

                  {/* 사업자등록증/민간자격증 — 교육 기관(provider)만 필수 */}
                  {memberType === 'provider' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        사업자등록증 또는 민간자격증 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,application/pdf"
                        onChange={handleCertificateChange}
                        className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border file:border-accent/40 file:bg-accent/5 file:text-accent file:font-medium hover:file:bg-accent/10"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        PDF·JPG·PNG, 최대 10MB. 관리자 검토 후 수업 등록이 가능합니다.
                      </p>
                      {certificateFile && (
                        <p className="text-xs text-gray-700 mt-1">
                          선택됨: {certificateFile.name} ({(certificateFile.size / 1024).toFixed(0)} KB)
                        </p>
                      )}
                    </div>
                  )}
                  <div className="border-t border-gray-200 my-6" />
                </>
              )}

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  이메일 <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('email')}
                  placeholder="example@email.com"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 ${
                    isProvider ? 'focus:ring-accent' : 'focus:ring-primary'
                  } focus:border-transparent outline-none ${
                    touched.email && fieldErrors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {touched.email && fieldErrors.email && (
                  <p className="mt-1 text-sm text-red-500">{fieldErrors.email}</p>
                )}
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  비밀번호 <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur('password')}
                    placeholder="영문, 숫자, 특수문자 포함 8자 이상"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 ${
                      isProvider ? 'focus:ring-accent' : 'focus:ring-primary'
                    } focus:border-transparent outline-none pr-12 ${
                      touched.password && fieldErrors.password ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {touched.password && fieldErrors.password && (
                  <p className="mt-1 text-sm text-red-500">{fieldErrors.password}</p>
                )}
              </div>

              {/* Password Confirm */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  비밀번호 확인 <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPasswordConfirm ? 'text' : 'password'}
                    name="passwordConfirm"
                    value={formData.passwordConfirm}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur('passwordConfirm')}
                    placeholder="비밀번호 재입력"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 ${
                      isProvider ? 'focus:ring-accent' : 'focus:ring-primary'
                    } focus:border-transparent outline-none pr-12 ${
                      touched.passwordConfirm && fieldErrors.passwordConfirm ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswordConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {touched.passwordConfirm && fieldErrors.passwordConfirm && (
                  <p className="mt-1 text-sm text-red-500">{fieldErrors.passwordConfirm}</p>
                )}
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isProvider ? '담당자 이름 (한글)' : '이름 (한글)'} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="홍길동"
                  className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 ${
                    isProvider ? 'focus:ring-accent' : 'focus:ring-primary'
                  } focus:border-transparent outline-none`}
                />
              </div>

              {/* English Name - 일반 회원만 표시 (기업 회원은 대표자 영문 이름 사용) */}
              {!isProvider && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name (이름) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur('firstName')}
                      placeholder="Gildong"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none ${
                        touched.firstName && fieldErrors.firstName ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {touched.firstName && fieldErrors.firstName && (
                      <p className="mt-1 text-sm text-red-500">{fieldErrors.firstName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name (성) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur('lastName')}
                      placeholder="Hong"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none ${
                        touched.lastName && fieldErrors.lastName ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {touched.lastName && fieldErrors.lastName && (
                      <p className="mt-1 text-sm text-red-500">{fieldErrors.lastName}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">예: Gildong Hong (인증서에 표시됩니다)</p>
                  </div>
                </>
              )}

              {/* Gender */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  성별 <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border rounded-lg cursor-pointer transition-colors ${
                      formData.gender === 'male'
                        ? isProvider ? 'border-accent bg-accent/5 text-accent' : 'border-primary bg-primary/5 text-primary'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <input
                      type="radio"
                      name="gender"
                      value="male"
                      checked={formData.gender === 'male'}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <span className="font-medium">남성</span>
                  </label>
                  <label
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border rounded-lg cursor-pointer transition-colors ${
                      formData.gender === 'female'
                        ? isProvider ? 'border-accent bg-accent/5 text-accent' : 'border-primary bg-primary/5 text-primary'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <input
                      type="radio"
                      name="gender"
                      value="female"
                      checked={formData.gender === 'female'}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <span className="font-medium">여성</span>
                  </label>
                </div>
              </div>

              {/* Birth Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  생년월일 <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <select
                    name="birthYear"
                    value={formData.birthYear}
                    onChange={(e) => setFormData((prev) => ({ ...prev, birthYear: e.target.value }))}
                    className={`flex-1 px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 ${
                      isProvider ? 'focus:ring-accent' : 'focus:ring-primary'
                    } focus:border-transparent outline-none bg-white`}
                  >
                    <option value="">년도</option>
                    {Array.from({ length: 80 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                      <option key={year} value={year}>
                        {year}년
                      </option>
                    ))}
                  </select>
                  <select
                    name="birthMonth"
                    value={formData.birthMonth}
                    onChange={(e) => setFormData((prev) => ({ ...prev, birthMonth: e.target.value }))}
                    className={`flex-1 px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 ${
                      isProvider ? 'focus:ring-accent' : 'focus:ring-primary'
                    } focus:border-transparent outline-none bg-white`}
                  >
                    <option value="">월</option>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                      <option key={month} value={month}>
                        {month}월
                      </option>
                    ))}
                  </select>
                  <select
                    name="birthDay"
                    value={formData.birthDay}
                    onChange={(e) => setFormData((prev) => ({ ...prev, birthDay: e.target.value }))}
                    className={`flex-1 px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 ${
                      isProvider ? 'focus:ring-accent' : 'focus:ring-primary'
                    } focus:border-transparent outline-none bg-white`}
                  >
                    <option value="">일</option>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                      <option key={day} value={day}>
                        {day}일
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  휴대폰 번호 <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="010-1234-5678"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 ${
                    isProvider ? 'focus:ring-accent' : 'focus:ring-primary'
                  } focus:border-transparent outline-none ${
                    fieldErrors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {fieldErrors.phone && (
                  <p className="mt-1 text-sm text-red-500">{fieldErrors.phone}</p>
                )}
              </div>

              {/* 시/도 선택 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  시/도 <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.province}
                  onChange={handleProvinceChange}
                  className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 ${
                    isProvider ? 'focus:ring-accent' : 'focus:ring-primary'
                  } focus:border-transparent outline-none bg-white`}
                >
                  <option value="">시/도 선택</option>
                  {getAllProvinces().map((province) => (
                    <option key={province} value={province}>
                      {province}
                    </option>
                  ))}
                </select>
              </div>

              {/* 시/군/구 선택 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  시/군/구 <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.district}
                  onChange={handleDistrictChange}
                  disabled={!formData.province}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 ${
                    isProvider ? 'focus:ring-accent' : 'focus:ring-primary'
                  } focus:border-transparent outline-none bg-white ${
                    !formData.province ? 'border-gray-200 bg-gray-50 cursor-not-allowed' : 'border-gray-300'
                  }`}
                >
                  <option value="">시/군/구 선택</option>
                  {formData.province && getDistricts(formData.province).map((district) => (
                    <option key={district} value={district}>
                      {district}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full py-4 rounded-lg font-medium text-white transition-colors mt-6 ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : isProvider ? 'bg-accent hover:bg-accent/90' : 'bg-primary hover:bg-primary-600'
                }`}
              >
                {loading ? '처리 중...' : '가입하기'}
              </button>
            </form>
          )}

          {/* Login Link */}
          <p className="text-center text-sm text-gray-500 mt-6">
            이미 계정이 있으신가요?{' '}
            <Link to="/login" className={`font-medium hover:underline ${isProvider ? 'text-accent' : 'text-primary'}`}>
              로그인
            </Link>
          </p>
        </div>
      </div>
      </div>
    </>
  );
}
